/**
 * Auto-updater bridge.
 *
 * The updater itself lives in main; the renderer only reports what main cannot
 * see (is a session running, did the backend switch the feature off) and shows
 * the state in Settings. Every call is a no-op outside Electron — the web and
 * Tauri builds have no updater to talk to.
 */

import { getPreloadApi } from './bridge'
import type { UpdaterState } from '../../../../shared/updater'

export type { UpdaterState } from '../../../../shared/updater'
export { INSTALL_REFUSED_BUSY } from '../../../../shared/updater'

export async function getUpdaterState(): Promise<UpdaterState | null> {
  const api = getPreloadApi()
  if (typeof api?.updater?.getState !== 'function') return null
  try {
    return await api.updater.getState()
  } catch {
    return null
  }
}

export function onUpdaterState(callback: (state: UpdaterState) => void): () => void {
  const api = getPreloadApi()
  if (typeof api?.updater?.onState !== 'function') return () => undefined
  return api.updater.onState(callback)
}

export async function setAutoUpdateEnabled(enabled: boolean): Promise<UpdaterState | null> {
  const api = getPreloadApi()
  if (typeof api?.updater?.setEnabled !== 'function') return null
  try {
    return await api.updater.setEnabled(enabled)
  } catch {
    return null
  }
}

export async function checkForUpdatesNow(): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.updater?.checkNow !== 'function') return
  await api.updater.checkNow().catch(() => undefined)
}

export async function installUpdateNow(): Promise<{ installed: boolean; reason?: string }> {
  const api = getPreloadApi()
  if (typeof api?.updater?.installNow !== 'function') {
    return { installed: false, reason: 'unsupported' }
  }
  try {
    return await api.updater.installNow()
  } catch (error) {
    return { installed: false, reason: error instanceof Error ? error.message : 'failed' }
  }
}

/** Skip the update install the app opened with: the operator needs the booth now. */
export async function skipLaunchInstall(): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.updater?.skipLaunchInstall !== 'function') return
  await api.updater.skipLaunchInstall().catch(() => undefined)
}

/**
 * Tell main whether a booth session is running, so it never restarts the app
 * mid-session. Called from the same two chokepoints as the booth telemetry.
 */
export function reportSessionActive(active: boolean): void {
  const api = getPreloadApi()
  if (typeof api?.updater?.setSessionActive !== 'function') return
  void api.updater.setSessionActive(active).catch(() => undefined)
}

/**
 * Tell main whether the booth is past its home screen. Every flow reports this,
 * including the custom ones whose Welcome page never starts a booth session.
 */
export function reportFlowActive(active: boolean): void {
  const api = getPreloadApi()
  if (typeof api?.updater?.setFlowActive !== 'function') return
  void api.updater.setFlowActive(active).catch(() => undefined)
}

/** Push the backend `auto_update` kill switch down to main. */
export function reportRemoteUpdateEnabled(enabled: boolean): void {
  const api = getPreloadApi()
  if (typeof api?.updater?.setRemoteEnabled !== 'function') return
  void api.updater.setRemoteEnabled(enabled).catch(() => undefined)
}
